/* eslint-disable demo-patterns/direct-args-reference */
import type { Meta, StoryFn } from '@storybook/react';
import { action } from '@storybook/addon-actions';
import { useState, useMemo, useEffect } from 'react';

import { HierarchicalAssignments } from '@pega/cosmos-react-work';
import type { TaskItemProps, AssignmentProps } from '@pega/cosmos-react-work';

import { AIButtonFormDemo } from '../../core/AIButton/AIButton.stories';

import MockHierarchicalAssignments, { task } from './HierarchicalAssignments.mock';

export default {
  title: 'Work/HierarchicalAssignments',
  component: HierarchicalAssignments
} as Meta;

interface HierarchicalAssignmentsProps {
  progress?: boolean;
  showAction?: boolean;
  name?: string;
  collapsed: boolean;
  showTask?: boolean;
  expandAllCases?: boolean;
}

export const HierarchicalAssignmentsDemo: StoryFn<HierarchicalAssignmentsProps> = (
  args: HierarchicalAssignmentsProps
) => {
  const [progress, setProgress] = useState(args.collapsed);
  const [open, setOpen] = useState<boolean>(true);

  useEffect(() => {
    setOpen(args.collapsed);
  }, [args.collapsed]);

  const onToggleCollapsed = (collapsed: boolean) => {
    setOpen(collapsed);
    if (collapsed) setProgress(true);
    else
      setTimeout(() => {
        setProgress(false);
      }, 2000);
  };

  return (
    <MockHierarchicalAssignments
      name={args.name}
      onToggleCollapsed={onToggleCollapsed}
      progress={args?.progress || progress}
      showAction={args?.showAction}
      collapsed={open}
      showTask={args.showTask}
      expandAllCases={args.expandAllCases}
    />
  );
};

HierarchicalAssignmentsDemo.args = {
  progress: false,
  showAction: true,
  collapsed: false,
  showTask: false,
  name: 'Hierarchical Assignments'
};

HierarchicalAssignmentsDemo.argTypes = {
  progress: { control: { type: 'boolean' } },
  showAction: { control: { type: 'boolean' } },
  name: { control: { type: 'text' } },
  showTask: { control: { type: 'boolean' } },
  collapsed: { control: { type: 'boolean' } }
};

export const HierarchicalAssignmentListDemo: StoryFn<HierarchicalAssignmentsProps> = (
  args: HierarchicalAssignmentsProps
) => {
  const [taskOpened, setTaskOpened] = useState<TaskItemProps>();
  const [progress, setProgress] = useState(args.collapsed);
  const [open, setOpen] = useState<boolean>(true);

  useEffect(() => {
    setOpen(args.collapsed);
  }, [args.collapsed]);

  const getTask = (id: string) => {
    return {
      ...task,
      id,
      name: `Task ${id}`,
      content: (
        <AIButtonFormDemo
          onCancel={() => setTaskOpened(undefined)}
          onSubmit={() => setTaskOpened(undefined)}
        />
      )
    };
  };

  const onToggleCollapsed = (collapsed: boolean) => {
    setOpen(collapsed);
    if (collapsed) setProgress(true);
    else
      setTimeout(() => {
        setProgress(false);
      }, 2000);
  };

  const onAssigneeClick = (id: AssignmentProps['assigneeId']) => {
    action(`Show Assignee's Profile (${id})`)();
  };

  const getAssignmentContent = (id: AssignmentProps['id']) => {
    setTaskOpened(getTask(id));
  };

  useEffect(() => {
    if (args.showTask) {
      getAssignmentContent('Assignment');
    } else {
      setTaskOpened(undefined);
    }
  }, [args.showTask]);

  const HierarchicalAssignmentsListMock = useMemo(() => {
    const assignmentAction = args.showAction ? { onOpen: getAssignmentContent } : {};
    return {
      assignments: [
        {
          id: 'ASSIGN-WORKLIST LOANPROC-WORK S-69001!SUBCASELEVEL11_FLOW_1_New45',
          processName: 'Develop',
          name: 'Complete Develop',
          sla: '2025-04-21T10:56:27.000Z',
          urgency: 95,
          assigneeName: 'Una Newton',
          assigneeId: 'una01',
          onAssigneeClick,
          ...assignmentAction
        },
        {
          id: 'ASSIGN-WORKLIST LOANPROC-WORK S-69001!SUBCASELEVEL11_FLOW_1_New45-1',
          name: 'Collect information',
          processName: 'Sprinting',
          sla: '2025-04-21T10:56:27.000Z',
          urgency: 95,
          assigneeName: 'Una Newton',
          assigneeId: 'una01',
          onAssigneeClick,
          ...assignmentAction
        },
        {
          id: 'ASSIGN-WORKLIST LOANPROC-WORK S-69001!SUBCASELEVEL11_FLOW_1_New45-2',
          name: 'Send references',
          sla: '2025-04-21T10:56:27.000Z',
          urgency: 95,
          assigneeName: 'Una Newton',
          assigneeId: 'una01',
          onAssigneeClick,
          ...assignmentAction
        }
      ]
    };
  }, [args.showAction]);
  return (
    <HierarchicalAssignments
      assignments={HierarchicalAssignmentsListMock.assignments}
      collapsed={open}
      onToggleCollapsed={onToggleCollapsed}
      openItem={taskOpened}
      progress={args?.progress || progress}
      name={args.name}
    />
  );
};

HierarchicalAssignmentListDemo.args = {
  progress: false,
  showAction: true,
  name: 'Assignment List',
  collapsed: false,
  showTask: false
};

HierarchicalAssignmentListDemo.argTypes = {
  progress: { control: { type: 'boolean' } },
  showAction: { control: { type: 'boolean' } },
  name: { control: { type: 'text' } },
  collapsed: { control: { type: 'boolean' } },
  showTask: { control: { type: 'boolean' } }
};
